import React, {Fragment} from 'react';
import {
    List,
    Datagrid,
    TextField,
    EmailField,
    BooleanField,
    EditButton,
    Edit,
    BulkDeleteWithConfirmButton,
    SimpleForm,
    TextInput,
    BooleanInput,
    Create, SelectInput, Toolbar, SaveButton, useNotify, useDataProvider, useRefresh, useUnselectAll, useTranslate, useRecordContext, FunctionField,
} from 'react-admin';
import {BulkActionProps} from "react-admin";
import {Button, Chip} from "@mui/material";
import {alpha, styled} from "@mui/material/styles";

/**
 * A button which, when clicked, will PATCH each selected user
 * to set is_blocked: true, then refresh the list.
 */
const BulkBlockButton = ({ selectedIds }) => {
  const notify       = useNotify();
  const dataProvider = useDataProvider();
  const refresh      = useRefresh();
  const unselectAll  = useUnselectAll();

  const handleClick = () => {
    Promise.all(
        selectedIds.map(id =>
            dataProvider.blockUser('users', {
              id
            })
        )
    )
        .then(() => {
          notify('Selected users have been blocked', { type: 'info' });
          unselectAll('users');
          refresh();
        })
        .catch(error => {
          notify(
              typeof error === 'string'
                  ? error
                  : error.message || 'Error blocking users',
              { type: 'error' }
          );
        });
  };

  return (
      <Fragment>
          <Button
          label="Block users"
          onClick={handleClick}
          disabled={selectedIds.length === 0}
          >Block users</Button>
      </Fragment>
  );
};

/**
 * A button which, when clicked, will PATCH each selected user
 * to set is_blocked: true, then refresh the list.
 */
const UnBulkBlockButton = ({ selectedIds }) => {
    const notify       = useNotify();
    const dataProvider = useDataProvider();
    const refresh      = useRefresh();
    const unselectAll  = useUnselectAll();

    const handleClick = () => {
        Promise.all(
            selectedIds.map(id =>
                dataProvider.unblockUser('users', {
                    id
                })
            )
        )
            .then(() => {
                notify('Selected users have been unblocked', { type: 'info' });
                unselectAll('users');
                refresh();
            })
            .catch(error => {
                notify(
                    typeof error === 'string'
                        ? error
                        : error.message || 'Error unblocking users',
                    { type: 'error' }
                );
            });
    };

    return (
        <Fragment>
            <Button
                label="Unblock users"
                onClick={handleClick}
                disabled={selectedIds.length === 0}
            >Unblock users</Button>
        </Fragment>
    );
};

/**
 * Combine the default Delete button with our Block button.
 */
const UserBulkActionButtons = (props) => (
    <>
      <BulkBlockButton {...props} />
      <UnBulkBlockButton {...props} />
    </>
);

const roleChoices = [
  { id: 'admin', name: 'Admin' },
  { id: 'user', name: 'User' },
];

const StyledSimpleForm = styled(SimpleForm)(({ theme }) => ({
  backgroundColor: '#fff',
  padding: '24px',
  borderRadius: '8px',
  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
  '& .MuiFormControl-root': {
    width: '100%',
    maxWidth: '600px',
    marginBottom: '16px'
  },
  '& .MuiInputBase-root': {
    width: '100%'
  }
}));

export const UserList = props => (
  <List {...props} bulkActionButtons={<UserBulkActionButtons />} perPage={25}>
    <Datagrid rowClick="edit">
      <TextField source="id" />
      <EmailField source="email" />
      <TextField source="first_name" />
      <TextField source="last_name" />
      <TextField source="role" />
      <FunctionField 
        source="is_blocked" 
        render={record => {
          const value = record.is_blocked;
          if (value === true || value === 'true' || value === 1 || value === '1') {
            return <Chip label="Zablokowany" color="error" size="small" />;
          }
          return <Chip label="Aktywny" color="success" size="small" />;
        }}
      />
      <EditButton />
    </Datagrid>
  </List>
);

export const UserEdit = props => (
  <Edit {...props} redirect="list" undoable={false} mutationMode="pessimistic">
    <StyledSimpleForm toolbar={<Toolbar><SaveButton /></Toolbar>}>
      <TextInput disabled source="id" />
      <TextInput source="email" fullWidth />
      <TextInput source="first_name" fullWidth />
      <TextInput source="last_name" fullWidth />
      <SelectInput source="role" choices={roleChoices} fullWidth />
      <SelectInput source="is_blocked" choices={[
        { id: false, name: 'Aktywny' },
        { id: true, name: 'Zablokowany' }
      ]} fullWidth />
    </StyledSimpleForm>
  </Edit>
);

export const UserCreate = props => (
  <Create {...props}>
    <StyledSimpleForm>
      <TextInput source="email" fullWidth />
      <TextInput source="first_name" fullWidth />
      <TextInput source="last_name" fullWidth />
      <SelectInput source="role" choices={roleChoices} defaultValue="user" fullWidth />
    </StyledSimpleForm>
  </Create>
);
